import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvoice, useSendInvoice, useMarkPaidInvoice, useVoidInvoice } from './hooks/useInvoices';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { SendToCustomerModal } from './SendToCustomerModal';
import Button from '../../shared/components/Button';
import type { InvoiceStatus } from './types';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../auth/store';
import { merchantsApi } from '../merchants/api/merchantsApi';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatAmount(kobo: number, currency = 'NGN') {
  const symbol = currency === 'NGN' ? '₦' : '$';
  return symbol + (kobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface Props {
  id: string;
}

export default function InvoiceDetail({ id }: Props) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { data: invoice, isLoading, error } = useInvoice(id);
  const sendMutation = useSendInvoice();
  const markPaidMutation = useMarkPaidInvoice();
  const voidMutation = useVoidInvoice();

  const { user } = useAuthStore();
  const isCollectiveStaff = user?.roleScope === 'COLLECTIVE';
  const { data: merchantData } = useQuery({
    queryKey: ['merchants', invoice?.tenantId],
    queryFn: () => merchantsApi.get(invoice!.tenantId),
    enabled: !!(isCollectiveStaff && invoice?.tenantId),
    staleTime: 2 * 60 * 1000,
  });

  const canUpdate = hasPermission(Permission.INVOICE_UPDATE);
  const canVoid = hasPermission(Permission.INVOICE_VOID);
  const [showSendToCustomer, setShowSendToCustomer] = useState(false);

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Loading invoice…</div>;
  }

  if (error || !invoice) {
    return <div className="p-8 text-center text-red-500">Invoice not found.</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/invoices')} className="text-muted-foreground hover:text-muted-foreground text-sm">
          ← Back to Invoices
        </button>
      </div>

      <div className="bg-card rounded-2xl border border-border p-8 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Invoice</p>
            <h1 className="text-2xl font-semibold text-foreground font-mono">{invoice.invoiceNumber}</h1>
          </div>
          <InvoiceStatusBadge status={invoice.status as InvoiceStatus} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4 border-t border-b border-border">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{isCollectiveStaff ? 'Merchant' : 'Tenant ID'}</p>
            <p className="text-sm font-medium text-foreground truncate" title={invoice.tenantId}>
              {isCollectiveStaff && merchantData ? merchantData.name : invoice.tenantId}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Solution</p>
            <p className="text-sm font-medium text-foreground">{invoice.solutionId}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Type</p>
            <p className="text-sm font-medium text-foreground">{invoice.type}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Issued</p>
            <p className="text-sm text-foreground">{formatDate(invoice.issuedAt)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Due Date</p>
            <p className="text-sm text-foreground">{formatDate(invoice.dueDate)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Paid</p>
            <p className="text-sm text-foreground">{formatDate(invoice.paidAt)}</p>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-foreground mb-3">Line Items</h2>
          {invoice.lineItems?.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="pb-2 font-medium">Description</th>
                  <th className="pb-2 font-medium text-right">Qty</th>
                  <th className="pb-2 font-medium text-right">Unit Price</th>
                  <th className="pb-2 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {invoice.lineItems.map((item) => (
                  <tr key={item.id}>
                    <td className="py-2 text-foreground">{item.description}</td>
                    <td className="py-2 text-right text-muted-foreground">{item.quantity}</td>
                    <td className="py-2 text-right text-muted-foreground">{formatAmount(item.unitAmount, invoice.currency)}</td>
                    <td className="py-2 text-right font-medium text-foreground">{formatAmount(item.totalAmount, invoice.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted-foreground">No line items.</p>
          )}
        </div>

        <div className="flex justify-end border-t border-border pt-4">
          <div className="space-y-1 text-sm text-right">
            <div className="flex gap-8 justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">{formatAmount(invoice.subtotal, invoice.currency)}</span>
            </div>
            {invoice.taxAmount > 0 && (
              <div className="flex gap-8 justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span className="font-medium">{formatAmount(invoice.taxAmount, invoice.currency)}</span>
              </div>
            )}
            <div className="flex gap-8 justify-between text-base font-semibold text-foreground pt-1 border-t border-border mt-1">
              <span>Total</span>
              <span>{formatAmount(invoice.totalAmount, invoice.currency)}</span>
            </div>
          </div>
        </div>

        {invoice.sentToCustomerAt && invoice.recipientEmail && (
          <p className="text-xs text-gray-400 border-t border-gray-100 pt-4">
            Last emailed to <span className="text-gray-600">{invoice.recipientEmail}</span> on {formatDate(invoice.sentToCustomerAt)}.
          </p>
        )}

        {(canUpdate || canVoid) && (
          <div className="flex flex-wrap gap-3 border-t border-border pt-4">
            {/* Email the invoice to a customer — the merchant→customer flow (ONE_TIME only). */}
            {canUpdate && invoice.type === 'ONE_TIME' && (
              <Button onClick={() => setShowSendToCustomer(true)}>
                Send to Customer
              </Button>
            )}
            {/* Tenant billing operations are TCS-staff only. */}
            {isCollectiveStaff && canUpdate && invoice.status === 'DRAFT' && (
              <Button
                onClick={() => sendMutation.mutate(invoice.id)}
                disabled={sendMutation.isPending}
              >
                {sendMutation.isPending ? 'Sending…' : 'Send Invoice'}
              </Button>
            )}
            {isCollectiveStaff && canUpdate && invoice.status === 'SENT' && (
              <Button
                onClick={() => markPaidMutation.mutate(invoice.id)}
                disabled={markPaidMutation.isPending}
              >
                {markPaidMutation.isPending ? 'Updating…' : 'Mark as Paid'}
              </Button>
            )}
            {canVoid && (invoice.status === 'DRAFT' || invoice.status === 'SENT') && (
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to void this invoice?')) {
                    voidMutation.mutate(invoice.id);
                  }
                }}
                disabled={voidMutation.isPending}
                className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                {voidMutation.isPending ? 'Voiding…' : 'Void Invoice'}
              </button>
            )}
          </div>
        )}
      </div>

      {showSendToCustomer && (
        <SendToCustomerModal invoice={invoice} onClose={() => setShowSendToCustomer(false)} />
      )}
    </div>
  );
}
